import DomSync from '../../../Core/helper/DomSync.js';
import TimelineChartProviderBase from './TimelineChartProviderBase.js';

/**
 * @module Gantt/util/chart/SVGChartProvider
 */

/**
 * Implementation of the chart provider using SVG elements.
 *
 * It will draw SVG path with optional dots for each dataset and axis lines. Axis lines could be drawn across
 * entire chart area or only on the left side. Distance between axis lines is calculated based on the chart height.
 *
 * @extends Gantt/util/chart/TimelineChartProviderBase
 */
export default class SVGChartProvider extends TimelineChartProviderBase {
    static configurable = {
        chartPathCls : 'b-timeline-chart-path',
        chartDotCls  : 'b-timeline-chart-point',
        chartAxisCls : 'b-timeline-chart-axis',

        tooltip : {
            cls         : 'b-timeline-chart-tooltip',
            forSelector : '.b-timeline-chart-point'
        },

        /**
         * When `true`, axis lines will be drawn across entire chart area.
         *
         * @prp {Boolean}
         * @category Chart
         */
        showAxisLines : false,

        /**
         * When `true`, points on the chart path will be shown separately.
         *
         * @prp {Boolean}
         * @default
         * @category Chart
         */
        showLineDetails : true,

        /**
         * Defines minimum distance in pixels between two axis lines. Based on this value chart will calculate how many
         * lines to draw. See corresponding config {@link #config-axisTicksCount}.
         *
         * @prp {Number}
         * @default
         * @category Chart
         */
        minAxisTickDistance : 50,

        /**
         * Defines zoom levels for chart axis lines. By default, axis is split into 10 sections. When lines are closer
         * than {@link #config-minAxisTickDistance} next zoom level will be used. Default values are [10, 5, 4, 2, 1]
         * which mean "draw line every 10% of the axis, if too close, then every 20%, 25%, 50% and 100% of the axis".
         *
         * @prp {Number[]}
         * @default
         * @category Chart
         */
        axisTicksCount : [10, 5, 4, 2, 1],

        circleRadius : 3
    };

    construct({ gantt, config }) {
        super.construct({ gantt, config });

        this.renderCanvas(gantt.timeAxisSubGridElement);
    }

    doDestroy() {
        this.tooltip?.destroy();

        super.doDestroy();
    }

    updateShowAxisLines() {
        if (!this.isConfiguring) {
            this.refresh();
        }
    }

    updateShowLineDetails() {
        if (!this.isConfiguring) {
            this.refresh();
        }
    }

    updateMinAxisTickDistance() {
        if (!this.isConfiguring) {
            this.refresh();
        }
    }

    updateAxisTicksCount() {
        if (!this.isConfiguring) {
            this.refresh();
        }
    }

    renderCanvas(parentElement) {
        const me = this;

        me.element?.remove();

        me.element = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

        me.element.classList.add('b-timeline-overlay-chart');
        me.element.setAttribute('width', me.width ?? '100%');
        me.element.setAttribute('height', me.height ?? '100%');
        me.element.style.zIndex = 10;

        parentElement?.appendChild(me.element);
    }

    updateCanvas() {
        super.updateCanvas();

        this.element.setAttribute('width', this.width ?? '100%');
        this.element.setAttribute('height', this.height ?? '100%');
    }

    draw() {
        const me = this;

        if (!me.dataset) {
            return;
        }

        const elements = [];

        if (me.dataset.length && me.dataset.some(dataset => dataset.lineData.length)) {
            elements.push(...me.getAxesRenderData());

            elements.push(...me.dataset.flatMap(dataset => me.getSeriesRenderData(dataset)));
        }

        DomSync.sync({
            targetElement : me.element,
            domConfig     : {
                children : elements
            }
        });

        me.trigger('draw');
    }

    clear() {
        if (this.element) {
            DomSync.sync({
                targetElement : this.element,
                domConfig     : {
                    children : []
                }
            });
        }
    }

    //#region Drawing

    getHorizontalCoordinate(x) {
        return this.gantt.rtl ? this.width - x : x;
    }

    getAxesRenderData() {
        const
            me        = this,
            chartArea = me.getChartViewCoordinates(),
            result    = [];

        if (me.showAxisLines) {
            result.push({
                tag   : 'line',
                ns    : 'http://www.w3.org/2000/svg',
                x1    : me.getHorizontalCoordinate(chartArea.x0),
                y1    : chartArea.y0,
                x2    : me.getHorizontalCoordinate(chartArea.x0),
                y2    : chartArea.y1,
                class : {
                    [me.chartAxisCls] : true
                }
            });

            // If we're showing lines, we need to draw the right boundary too
            result.push({
                tag   : 'line',
                ns    : 'http://www.w3.org/2000/svg',
                x1    : me.getHorizontalCoordinate(chartArea.x1),
                y1    : chartArea.y0,
                x2    : me.getHorizontalCoordinate(chartArea.x1),
                y2    : chartArea.y1,
                class : {
                    [me.chartAxisCls] : true
                }
            });

            const ticksCount = this.axisTicksCount.find(count => {
                return Math.floor(chartArea.y1 - chartArea.y0) / count >= this.minAxisTickDistance;
            }) || 1;

            for (let i = 0; i <= ticksCount; i++) {
                const position = chartArea.y1 - i * (chartArea.y1 - chartArea.y0) / ticksCount;

                result.push({
                    tag   : 'line',
                    ns    : 'http://www.w3.org/2000/svg',
                    x1    : me.getHorizontalCoordinate(chartArea.x0),
                    y1    : position,
                    x2    : me.getHorizontalCoordinate(me.showAxisLines ? chartArea.x1 : 10),
                    y2    : position,
                    class : {
                        [me.chartAxisCls] : true
                    }
                });
            }
        }

        return result;
    }

    getSeriesRenderData(series) {
        if (!series.lineData.length) {
            return;
        }

        const
            me                = this,
            chartArea         = me.getChartViewCoordinates(),
            lineVerticalScale = (chartArea.y1 - chartArea.y0) / series.maxValue,
            result            = [];

        const pathDef = {
            tag : 'path',
            ns  : 'http://www.w3.org/2000/svg',
            d   : `M${me.getHorizontalCoordinate(chartArea.x0)},${chartArea.y1} ` + series.lineData.reduce((path, item) => {
                return path + `L${item.x} ${Math.round(chartArea.y1 - item.y * lineVerticalScale)} `;
            }, ''),
            class : {
                [me.chartPathCls] : true
            },
            dataset : {
                name : series.name
            }
        };

        if (series.color) {
            pathDef.style = { stroke : series.color };
        }

        result.push(pathDef);

        if (me.showLineDetails) {
            series.lineData.forEach((item, index) => {
                const y = chartArea.y1 - item.y * lineVerticalScale;

                const circleDef = {
                    tag   : 'circle',
                    ns    : 'http://www.w3.org/2000/svg',
                    cx    : item.x,
                    cy    : y,
                    r     : me.circleRadius,
                    class : {
                        [me.chartDotCls] : true
                    }
                };

                result.push(circleDef);

                if (series.color) {
                    circleDef.style = { fill : series.color };
                }

                const dataset = {
                    index,
                    name  : series.name,
                    label : series.label
                };

                Object.keys(item).forEach(key => {
                    if (key !== 'x' && key !== 'y') {
                        dataset[key] = item[key];
                    }
                });

                circleDef.dataset = dataset;
            });
        }

        return result;
    }

    //#endregion
}
